import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { getRecommendedPlan } from "../lib/upgradeCta";

/**
 * Tiny amber chip that nudges the coach toward an upgrade when they're at
 * ≥85% of a plan cap, but does NOT block them. Sits next to action buttons
 * (Add Player / New Team) so the upgrade path is one tap away at the moment
 * of friction.
 *
 * Self-contained: fetches /orgs/mine/plan on mount and caches in module state
 * so it doesn't refetch on every render across pages.
 *
 * Props
 *   kind: "athletes" | "teams"
 *   terms: termsFor(account_type) — for the right wording
 *   threshold: 0..1 (default 0.85)
 *   testid: data-testid override
 */

// Module-level cache so multiple tips on the same page don't double-fetch
let _cache = null;
let _inflight = null;
async function loadPlan() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = api
    .get("/orgs/mine/plan")
    .then((r) => {
      _cache = r.data;
      _inflight = null;
      return _cache;
    })
    .catch(() => {
      _inflight = null;
      return null;
    });
  return _inflight;
}

/** Public reset — call after a mutation that may change usage (archive, add). */
export function invalidateNearLimitCache() {
  _cache = null;
}

export default function NearLimitTip({ kind, terms, threshold = 0.85, testid }) {
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    let mounted = true;
    loadPlan().then((p) => { if (mounted) setPlan(p); });
    return () => { mounted = false; };
  }, []);

  if (!plan) return null;
  const limits = plan.limits || {};
  const usage = plan.usage || {};

  const value = kind === "teams" ? (usage.active_teams || 0) : (usage.active_athletes || 0);
  const limit = kind === "teams" ? limits.teams : limits.athletes;

  if (limit == null) return null; // unlimited (Enterprise) — never show
  if (value < Math.ceil(limit * threshold)) return null; // not close enough

  // Wording per spec:
  //   At/over limit (e.g. 4/4):  "Team limit reached: 4/4 active teams. Upgrade to add another team."
  //                              "Player limit reached: 25/25 active players. Upgrade to add more players."
  //   Approaching (e.g. 22/25):  "Approaching team limit: 4/5 active teams. Upgrade to add more."
  //                              "Approaching player limit: 22/25 active players. Upgrade to add more."
  const atLimit = value >= limit;
  const isTeams = kind === "teams";
  const noun = isTeams
    ? (limit === 1 ? (terms.team || "team") : (terms.teams || "teams"))
    : (terms.athletes || "players");

  if (plan.early_access) {
    const eaLabel = atLimit
      ? `Early Access limit reached: ${value}/${limit} active ${noun}.`
      : `Approaching the Early Access limit: ${value}/${limit} active ${noun}.`;
    return (
      <div
        data-testid={testid || `near-limit-tip-${kind}`}
        className="inline-flex max-w-full items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900"
        role="status"
        aria-live="polite"
      >
        <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-600" />
        <span className="truncate">{eaLabel}</span>
        <a
          href="mailto:support@squadfee.com?subject=Squad%20Fee%20Early%20Access%20limit"
          className="shrink-0 font-bold text-amber-700 underline-offset-2 hover:underline"
          data-testid={`near-limit-tip-${kind}-contact`}
        >
          Contact support
        </a>
      </div>
    );
  }
  const label = atLimit
    ? (isTeams
        ? `You've reached your ${terms.team || "team"} limit. Upgrade to add another ${terms.team || "team"}.`
        : `${(terms.Athlete || "Player")} limit reached: ${value}/${limit} active ${noun}. Upgrade to add more ${noun}.`)
    : (isTeams
        ? `Approaching ${terms.team || "team"} limit: ${value}/${limit} active ${noun}. Upgrade to add more.`
        : `Approaching ${terms.athlete || "player"} limit: ${value}/${limit} active ${noun}. Upgrade to add more.`);

  const recommended = getRecommendedPlan(plan.plan);
  const href = recommended
    ? `/pricing?recommended=${encodeURIComponent(recommended)}`
    : "/pricing";

  return (
    <div
      data-testid={testid || `near-limit-tip-${kind}`}
      className="inline-flex max-w-full items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900"
      role="status"
      aria-live="polite"
    >
      <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-600" />
      <span className="truncate">{label}</span>
      <Link
        to={href}
        className="shrink-0 font-bold text-amber-700 underline-offset-2 hover:underline"
        data-testid={`near-limit-tip-${kind}-upgrade`}
      >
        Upgrade
      </Link>
    </div>
  );
}
